import React, { useEffect, useState } from 'react';
import { Alert, DatePicker, Form, Input, Modal, Radio } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { countNoticeRecipients, type OrderConfirmationNoticeSelection } from '../confirmationNoticeSelection';
import OrderConfirmDialogBody from './guide/OrderConfirmDialogBody';
import { DEFAULT_FULFILLMENT_CHOICE, type OrderFulfillmentChoice } from './guide/ConfirmFulfillmentBlock';
import { buildOfferAcceptanceNote, type OfferAcceptance } from './orderOfferAcceptance';
import type { OrderStatusFormValues } from '../useOrderStatusChangeWithConfirmation';
import type { OrderProduct } from '../../../types/order';

/**
 * „Potwierdzenie zamówienia <nr>” (Oferta → Potwierdzone, first mockup
 * `confirm` modal): the stock checked now, who is notified (cards with a
 * checkbox), „Skąd realizujemy?” and the draft ZD for Valentyna
 * (`guide/OrderConfirmDialogBody`),
 * plus the customer's acceptance from the Oferta screen, which goes into the
 * status note. Nothing here blocks — an unticked acceptance only shows a
 * warning and nobody ticked still confirms (the button then says so).
 */
export function OrderConfirmDialog({
  open,
  orderId,
  orderNumber,
  acceptance,
  initialFulfillment,
  products,
  submitting,
  onCancel,
  onSubmit,
}: {
  open: boolean;
  orderId: string;
  /** Order lines, for the dimensions under the product names of the stock table. */
  products?: OrderProduct[];
  orderNumber: string;
  acceptance: OfferAcceptance;
  /** The choice stored on the ZK: a repeated confirmation starts from it, not from the default. */
  initialFulfillment?: OrderFulfillmentChoice;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (values: OrderStatusFormValues) => void;
}) {
  const { t } = useTranslation(['orders']);
  const [notice, setNotice] = useState<OrderConfirmationNoticeSelection | null>(null);
  // „Skąd realizujemy?” (owner 2026-10-05): the warehouse unless the Handlowiec switches.
  const [fulfillment, setFulfillment] = useState<OrderFulfillmentChoice>(DEFAULT_FULFILLMENT_CHOICE);
  const note = buildOfferAcceptanceNote(acceptance);
  useEffect(() => {
    if (open) {
      setFulfillment(initialFulfillment ?? DEFAULT_FULFILLMENT_CHOICE);
    } else {
      setNotice(null);
      setFulfillment(DEFAULT_FULFILLMENT_CHOICE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // The button says what it does and for how many: „Potwierdź i powiadom (N)”.
  const recipients = notice ? countNoticeRecipients(notice) : 0;
  return (
    <Modal
      open={open}
      title={t('orders:process.confirmDialog.title', { orderNumber })}
      okText={t(recipients > 0 ? 'orders:process.confirmDialog.submitAndNotify' : 'orders:process.confirmDialog.submitSilent', { count: recipients })}
      okButtonProps={{ 'data-testid': 'zk-confirm-submit' } as Record<string, string>}
      cancelText={t('common:cancel')}
      confirmLoading={submitting}
      onCancel={onCancel}
      onOk={() => onSubmit({
        status: 'CONFIRMED',
        notes: note || undefined,
        ...(notice ? { confirmationNotice: notice } : {}),
        fulfillmentSource: fulfillment.source,
        ...(fulfillment.note.trim() ? { fulfillmentNote: fulfillment.note.trim() } : {}),
      })}
      destroyOnClose
      width={640}
      // Long stock tables scroll inside the window: the button stays on screen.
      styles={{ body: { maxHeight: 'calc(100vh - 240px)', overflowY: 'auto' } }}
    >
      {!acceptance.accepted && (
        <Alert type="warning" showIcon className="zk-dialog-alert" message={t('orders:process.confirmDialog.notAccepted')} />
      )}
      {note && <Alert type="info" showIcon className="zk-dialog-alert" message={t('orders:process.confirmDialog.note')} description={note} />}
      <OrderConfirmDialogBody
        orderId={orderId}
        value={notice}
        onChange={setNotice}
        fulfillment={fulfillment}
        onFulfillmentChange={setFulfillment}
        products={products}
      />
    </Modal>
  );
}

/**
 * „Potwierdź dostarczenie” (W dostawie → Dostarczone / Częściowo dostarczone).
 * The day of delivery is chosen here (default today) and stored with
 * DELIVERED (`actualDelivery`, F6). Without a released WZ the backend asks for
 * the usual confirmed reason (operational exception), never a block.
 */
export function OrderDeliverDialog({
  open,
  allIssued,
  alreadyPartial = false,
  submitting,
  onCancel,
  onSubmit,
}: {
  open: boolean;
  allIssued: boolean;
  /** The ZK is already „Częściowo dostarczone”: only the whole delivery is left to confirm. */
  alreadyPartial?: boolean;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (values: OrderStatusFormValues) => void;
}) {
  const { t } = useTranslation(['orders', 'common']);
  const initialStatus = allIssued || alreadyPartial ? 'DELIVERED' : 'PARTIALLY_DELIVERED';
  const [status, setStatus] = useState<'DELIVERED' | 'PARTIALLY_DELIVERED'>(initialStatus);
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [notes, setNotes] = useState('');
  useEffect(() => {
    if (open) {
      setStatus(initialStatus);
      setDate(dayjs());
      setNotes('');
    }
  }, [open, initialStatus]);
  return (
    <Modal
      open={open}
      title={t('orders:process.deliverDialog.title')}
      okText={t('orders:process.bar.confirmDelivery')}
      cancelText={t('common:cancel')}
      confirmLoading={submitting}
      onCancel={onCancel}
      onOk={() => onSubmit({
        status,
        notes: notes.trim() || undefined,
        ...(status === 'DELIVERED' ? { actualDelivery: date.format('YYYY-MM-DD') } : {}),
      })}
      destroyOnClose
    >
      <Form layout="vertical" component="div">
        <Form.Item label={t('orders:process.deliverDialog.scope')}>
          <Radio.Group value={status} onChange={(event) => setStatus(event.target.value)}>
            <Radio value="DELIVERED">{t('orders:process.deliverDialog.full')}</Radio>
            <Radio value="PARTIALLY_DELIVERED" disabled={alreadyPartial}>
              {t(alreadyPartial ? 'orders:process.deliverDialog.partialCurrent' : 'orders:process.deliverDialog.partial')}
            </Radio>
          </Radio.Group>
        </Form.Item>
        {!allIssued && status === 'DELIVERED' && (
          <Alert type="warning" showIcon className="zk-dialog-alert" message={t('orders:process.deliverDialog.notAllIssued')} />
        )}
        {status === 'DELIVERED' && (
          <Form.Item label={t('orders:process.deliverDialog.date')} required>
            <DatePicker
              format="DD.MM.YYYY"
              value={date}
              allowClear={false}
              disabledDate={(day) => day.isAfter(dayjs(), 'day')}
              onChange={(value) => value && setDate(value)}
              aria-label={t('orders:process.deliverDialog.date')}
            />
          </Form.Item>
        )}
        <Form.Item label={t('orders:process.deliverDialog.notes')}>
          <Input.TextArea rows={3} maxLength={500} showCount value={notes} placeholder={t('orders:process.deliverDialog.notesPlaceholder')} onChange={(event) => setNotes(event.target.value)} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
